// The Spec list's URL state: the source's `?sort=` encoding, and the arithmetic
// the pager runs on. No React, no router and no wire names — `lib/` sits below
// both graphs (`client/docs/architecture.md`, "The dependency rule"). Which
// fields a tab offers, and what the wire calls them, is the browser contract's
// (`@/api/contracts/specifications`), which reads these helpers.
//
// Everything here is total. The URL is user-editable, so a malformed `?sort=`
// or `?filters=` is an ordinary case the parser reads as best it can, never an
// error.

export type SpecListSortDirection = "asc" | "desc";

/** One `?sort=` segment as the URL spells it: the source's Django field name. */
export interface SpecListUrlSort {
  field: string;
  direction: SpecListSortDirection;
}

/**
 * The source's `parseSort` (`useSpecsSortState.ts:62-80`). `undefined` — no
 * `?sort=` at all — is **absent** and answers `null`, which the page reads as
 * "take the tab's default"; the empty string is the **explicit empty** the
 * Clear all button writes, and answers `[]`. The two are different pages on
 * Core, whose default is Name A→Z.
 *
 * Segments are comma-joined and trimmed, empty ones dropped, a leading `-`
 * descending; a field named twice keeps its first segment.
 */
export function parseSpecListSort(raw: string | undefined): SpecListUrlSort[] | null {
  if (raw === undefined) return null;
  const seen = new Set<string>();
  const sort: SpecListUrlSort[] = [];
  for (const segment of raw.split(",")) {
    const trimmed = segment.trim();
    const desc = trimmed.startsWith("-");
    const field = desc ? trimmed.slice(1) : trimmed;
    if (field === "" || seen.has(field)) continue;
    seen.add(field);
    sort.push({ field, direction: desc ? "desc" : "asc" });
  }
  return sort;
}

/** The inverse (`serializeSort`, `:82-89`). No sort writes `""`, the sentinel. */
export function serializeSpecListSort(sort: readonly SpecListUrlSort[]): string {
  return sort.map(({ field, direction }) => (direction === "desc" ? `-${field}` : field)).join(",");
}

/**
 * The navigation a sort write makes (`useSpecsSortState.ts:118-131`): the new
 * `?sort=` merged over the params already there, **replacing** the history
 * entry — so Back never replays a sort click — and the explicit `""` written as
 * itself rather than dropped. Router-free, so the route and its routing test
 * hand the same object to `navigate`.
 */
export function specListSortNavigation(rawSort: string) {
  return {
    search: <Search extends object>(prev: Search) => ({ ...prev, sort: rawSort }),
    replace: true,
    // The window stays where it was — see `specListFiltersNavigation`.
    resetScroll: false,
  } as const;
}

/**
 * The source's `(Blank)` value, exactly (`distinct_values/handlers.py:100`,
 * `useSpecsColumnFiltersState.ts:32`): a NUL, then `__null__` — what an `in`
 * filter's values carry in the URL for "no value".
 */
export const SPEC_FILTER_BLANK_SENTINEL = "\u0000__null__";

/** The operators `?filters=` may carry (`useSpecsColumnFiltersState.ts`). */
export const SPEC_LIST_URL_FILTER_OPERATORS = [
  "in",
  "icontains",
  "istartswith",
  "gte",
  "lte",
] as const;

export type SpecListUrlFilterOperator = (typeof SPEC_LIST_URL_FILTER_OPERATORS)[number];

/**
 * One `?filters=` item as the URL spells it: the source's Django field name,
 * one of the five operators, and string values — the `(Blank)` sentinel among
 * them, raw, for an `in`.
 */
export interface SpecListUrlFilter {
  field: string;
  operator: SpecListUrlFilterOperator;
  values: readonly string[];
}

const isOperator = (value: unknown): value is SpecListUrlFilterOperator =>
  (SPEC_LIST_URL_FILTER_OPERATORS as readonly unknown[]).includes(value);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * The source's `parseColumnFilters`. `undefined` — no `?filters=` — is
 * **absent** and answers `null`; the empty string, unreadable JSON and JSON
 * that is not a list are all no filters.
 *
 * Each item needs a string `field` and one of the five operators; `values`
 * keeps only its non-empty strings, and a lone non-empty string is lifted to a
 * one-item list; an item left with no values is dropped. A second item on the
 * same field and operator is a duplicate, the first standing — and only an
 * item that kept a value claims its key, so an empty one never shadows a
 * later valid one. Two operators on one field (a date range) are two items.
 */
export function parseSpecListFilters(raw: string | undefined): SpecListUrlFilter[] | null {
  if (raw === undefined) return null;
  if (raw === "") return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const seen = new Set<string>();
  const filters: SpecListUrlFilter[] = [];
  for (const item of parsed) {
    if (!isRecord(item)) continue;
    const { field, operator, values: rawValues } = item;
    if (typeof field !== "string" || field === "") continue;
    if (!isOperator(operator)) continue;
    const values = Array.isArray(rawValues)
      ? rawValues.filter((value): value is string => typeof value === "string" && value !== "")
      : typeof rawValues === "string" && rawValues !== ""
        ? [rawValues]
        : [];
    if (values.length === 0) continue;
    const key = `${field}|${operator}`;
    if (seen.has(key)) continue;
    seen.add(key);
    filters.push({ field, operator, values });
  }
  return filters;
}

/** The inverse (`serializeColumnFilters`): a filter with no values is dropped. */
export function serializeSpecListFilters(filters: readonly SpecListUrlFilter[]): string {
  return JSON.stringify(
    filters
      .filter((filter) => filter.values.length > 0)
      .map(({ field, operator, values }) => ({ field, operator, values })),
  );
}

/**
 * The navigation a filter write makes (`useSpecsColumnFiltersState.ts`'s
 * `writeFilters`): the new `?filters=` merged over the params already there,
 * **pushing** a history entry — so Back steps through filter edits, where a
 * sort write replaces — and an empty list **deleting** the param rather than
 * writing `[]`. Router-free, like the sort's.
 *
 * **Every write pushes, one that changes nothing included**, as the donor's
 * `setSearchParams` does (react-router 7's `BrowserRouter` pushes on every
 * navigate): re-applying the same selection leaves an entry Back steps over.
 * TanStack skips a navigation to the href and history state it is already
 * at, so each write carries a fresh `specListFiltersWrite` token in the
 * entry's state.
 *
 * **Neither write moves the window** (`resetScroll: false`, here and on the
 * sort's): the donor's `BrowserRouter` restores no scroll and its one
 * scroll-to-top runs on a pathname change (`admin-layout.tsx:67-70`), where
 * TanStack's scroll restoration sends every navigation to the top.
 */
declare module "@tanstack/history" {
  interface HistoryState {
    /** A filter write's token — see `specListFiltersNavigation`. */
    specListFiltersWrite?: string;
  }
}

export function specListFiltersNavigation(filters: readonly SpecListUrlFilter[]) {
  const kept = filters.filter((filter) => filter.values.length > 0);
  const rawFilters = kept.length > 0 ? serializeSpecListFilters(kept) : undefined;
  return {
    search: <Search extends object>(prev: Search) => ({ ...prev, filters: rawFilters }),
    state: { specListFiltersWrite: crypto.randomUUID() },
    replace: false,
    resetScroll: false,
  } as const;
}

/**
 * The page a page-size change lands on: the one holding the row that was at
 * the top. TanStack's `setPageSize`, which the donor's footer runs
 * (`RowPagination.js:88-98`): `floor(oldSize × oldIndex / newSize)`, zero-based
 * there and one-based here. It does not reset to page 1.
 */
export function pageAfterPageSizeChange(page: number, oldSize: number, newSize: number): number {
  return Math.floor((oldSize * (page - 1)) / newSize) + 1;
}
